import type { ProviderIdInput } from "@postdaily/api/schemas/social-accounts";
import { cn } from "@postdaily/ui/lib/utils";

/**
 * Every platform we render a brand mark for.
 *
 * This was `ProviderIdInput | "tiktok" | "threads" | "youtube" | "pinterest"`,
 * from when those four had no adapter and existed only to make the connect
 * grid look complete. They ship now and are in the `provider_id` enum that
 * `ProviderIdInput` derives from, so the extra arm had become pure duplication
 * — and the `as PlatformId` casts it forced were laundering values that were
 * already exactly this type.
 */
export type PlatformId = ProviderIdInput;

/**
 * Brand mark for a social provider, rendered Buffer-style as a rounded badge
 * with the provider's colour and a white glyph. Used anywhere a channel is
 * listed (sidebar, composer chips, account cards, connect flow).
 */
/**
 * A provider's official artwork, traced from the vendor's own vector file
 * rather than redrawn, so its proportions cannot drift.
 */
export type ProviderMark = {
  /** viewBox tightly bounding the artwork, so height alone can size it. */
  viewBox: string;
  /** width ÷ height of that viewBox. */
  aspect: number;
  /** Drawn in order; each carries its own fill, so there is no `currentColor`. */
  paths: readonly { d: string; fill: string }[];
};

export type ProviderVisual = {
  /** Badge background colour. */
  bg: string;
  /** 24×24 single-path glyph. */
  path: string;
  /**
   * The provider's own complete artwork, for a brand whose mark is not square.
   *
   * When present this is drawn as-is and `bg` is ignored. Redrawing such a mark
   * as a coloured square with a white glyph changes its proportions, which is
   * exactly what YouTube's brand site lists under "Don't squash the YouTube
   * Icon" — and policy III.F.2a-b asks for shape, colour AND height.
   */
  mark?: ProviderMark;
};

/**
 * Brand colour + glyph for every platform, keyed by {@link PlatformId}. Exported
 * so other surfaces (e.g. the marketing site's channel tiles and monochrome
 * social links) can reuse the exact same SVG paths instead of re-copying them.
 */
/**
 * YouTube's icon, taken from the official asset pack (brand.youtube → "Core
 * YouTube icon", Digital/01 Red) rather than redrawn: the path data below is
 * the vendor file's own, so the shape is theirs to the decimal.
 *
 * The body path carries the triangle as a sub-path and the triangle is then
 * painted white on top — that is how the source file is built, and it keeps the
 * triangle opaque instead of punching a hole through to whatever is behind.
 */
export const YOUTUBE_MARK: ProviderMark = {
  viewBox: "102.6875 119.167969 396 277.402344",
  aspect: 396 / 277.402344,
  paths: [
    {
      d: "M 261.203125 317.3125 L 363.890625 257.722656 L 261.203125 198.425781 Z M 146.078125 388.179688 C 129.011719 383.550781 115.707031 369.957031 110.789062 353.179688 C 102.6875 322.226562 102.6875 257.722656 102.6875 257.722656 C 102.6875 257.722656 102.6875 193.21875 110.789062 162.558594 C 115.707031 145.492188 129.011719 132.183594 146.078125 127.554688 C 176.738281 119.167969 300.542969 119.167969 300.542969 119.167969 C 300.542969 119.167969 424.636719 119.167969 455.296875 127.554688 C 472.652344 132.183594 485.667969 145.492188 490.296875 162.558594 C 498.6875 193.21875 498.6875 257.722656 498.6875 257.722656 C 498.6875 257.722656 498.6875 322.226562 490.296875 353.179688 C 485.667969 369.957031 472.652344 383.550781 455.296875 388.179688 C 424.636719 396.570312 300.542969 396.570312 300.542969 396.570312 C 300.542969 396.570312 176.738281 396.570312 146.078125 388.179688",
      fill: "#FF0033",
    },
    {
      d: "M 261.203125 317.3125 L 363.890625 257.722656 L 261.203125 198.425781 Z",
      fill: "#FFFFFF",
    },
  ],
};

export const PROVIDER_BRAND = {
  bluesky: {
    bg: "#0285FF",
    path: "M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z",
  },
  x: {
    bg: "#000000",
    path: "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z",
  },
  linkedin: {
    bg: "#0A66C2",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  },
  facebook: {
    bg: "#1877F2",
    path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  instagram: {
    bg: "#E4405F",
    path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  },
  mastodon: {
    bg: "#6364FF",
    path: "M23.268 5.313c-.35-2.578-2.617-4.61-5.304-5.004C17.51.242 15.792 0 11.813 0h-.03c-3.98 0-4.835.242-5.288.309C3.882.692 1.496 2.518.917 5.127.64 6.412.61 7.837.661 9.143c.073 1.874.087 3.745.257 5.611.118 1.24.325 2.47.62 3.68.55 2.237 2.777 4.098 4.96 4.857 2.336.792 4.849.923 7.256.38.265-.061.527-.132.786-.213.585-.184 1.27-.39 1.774-.753a.057.057 0 0 0 .023-.043v-1.809a.052.052 0 0 0-.02-.041.053.053 0 0 0-.046-.01 20.282 20.282 0 0 1-4.709.545c-2.73 0-3.463-1.284-3.674-1.818a5.593 5.593 0 0 1-.319-1.433.053.053 0 0 1 .066-.054c1.517.363 3.072.546 4.632.546.376 0 .75 0 1.125-.01 1.57-.044 3.224-.124 4.768-.422.038-.008.077-.015.11-.024 2.435-.464 4.753-1.92 4.989-5.604.008-.145.03-1.52.03-1.67.002-.512.167-3.63-.024-5.545zm-3.748 9.195h-2.561V8.29c0-1.309-.55-1.976-1.67-1.976-1.23 0-1.846.79-1.846 2.35v3.403h-2.546V8.663c0-1.56-.617-2.35-1.848-2.35-1.112 0-1.668.668-1.67 1.977v6.218H4.822V8.102c0-1.31.337-2.35 1.011-3.12.696-.77 1.608-1.164 2.74-1.164 1.311 0 2.302.5 2.962 1.498l.638 1.06.638-1.06c.66-.999 1.65-1.498 2.96-1.498 1.13 0 2.043.395 2.74 1.164.675.77 1.012 1.81 1.012 3.12z",
  },
  mock: {
    bg: "#6B7280",
    // Simple beaker glyph — mock is a deterministic test provider.
    path: "M9 2v6.5L4.2 17.3A2 2 0 0 0 6 20h12a2 2 0 0 0 1.8-2.7L15 8.5V2H9zm2 2h2v5l1.2 2.2H9.8L11 9V4z",
  },
  tiktok: {
    bg: "#000000",
    path: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  },
  threads: {
    bg: "#000000",
    path: "M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.85 13.85 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Z",
  },
  youtube: {
    // #FF0033 is YouTube's brand red (brand.youtube/color). Not #FF0000, which
    // is what we shipped until the audit — their own asset samples #FF0033.
    bg: "#FF0033",
    // Full silhouette, for the monochrome glyph. The colour rendering comes
    // from `mark`.
    path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
    mark: YOUTUBE_MARK,
  },
  pinterest: {
    bg: "#BD081C",
    path: "M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z",
  },
} satisfies Record<PlatformId, ProviderVisual>;

/**
 * Badge box and glyph size per step. The glyph size is a class ON the `<svg>`,
 * not only a `[&>svg]:` variant on the badge, for two reasons:
 *
 * 1. shadcn's interactive surfaces (button, sidebar menu button, command item)
 *    all carry `[&_svg:not([class*='size-'])]:size-4`. That descendant selector
 *    outranks the badge's own `[&>svg]:size-*`, so a class-less glyph inside any
 *    of them rendered at 16px — overflowing e.g. the 14px channel-avatar badge.
 *    A sparse mark showed it worst: Bluesky's butterfly grew past its blue
 *    square until only slivers of background were left, reading as a broken
 *    logo. Carrying a `size-*` class excludes the glyph from that `:not()`.
 * 2. A caller that overrides with `[&>svg]:size-2` still wins — a variant
 *    selector (`.[&>svg]:size-2 > svg`) outranks a plain class on the element.
 *
 * SIZE FLOOR — do not shrink these. YouTube's compliance review (2026-09-02,
 * policy III.F.2a-b) failed us for rendering its mark below the 20dp minimum;
 * the badges were 14-20px with 8-16px glyphs.
 *
 * For a provider with its own `mark` the height that must clear 20dp is the
 * artwork itself, so `mark` here is a height and the width follows from the
 * vendor's aspect ratio. For the rest the badge is a plain coloured tile
 * carrying a white glyph, and the badge is the thing that must clear 20dp.
 *
 * We previously drew YouTube the second way — a red SQUARE with a white
 * triangle. That cleared 20dp but restated their icon at 1:1 instead of its
 * own 1.43:1, which is the "Don't squash the YouTube Icon" case on their brand
 * site. Do not go back to it, and do not add a step below 24px.
 */
const sizeClasses = {
  // 20 is the policy floor exactly. The mark is 1.43:1, so its width is what
  // grows, not its height — 20 keeps the badge as small as it is allowed to be
  // on a 40px avatar.
  sm: { badge: "size-6", glyph: "size-3", mark: 20 },
  md: { badge: "size-7", glyph: "size-3.5", mark: 28 },
  lg: { badge: "size-8", glyph: "size-4", mark: 32 },
} as const;

/**
 * Draws a provider's official artwork at `className`'s height, width following
 * from the vendor aspect ratio. No tile, no radius, no ring: a container would
 * reshape the mark and a ring would outline it, both of which YouTube lists
 * under Misuse.
 */
export function ProviderMarkSvg({
  mark,
  height,
  className,
}: {
  mark: ProviderMark;
  /**
   * Pixel height. Both dimensions are then set explicitly, because an `auto`
   * width resolves by shrink-to-fit whenever the mark sits in an absolutely
   * positioned or flex context — which ignores the aspect ratio and squashed
   * the avatar badge to 16×20 on staging. Omit only when the caller sizes the
   * mark relative to its slot (BrandMark, the onboarding tiles).
   */
  height?: number;
  className?: string;
}) {
  // The 20dp floor from policy III.F.2a-b, applied here so no caller can drop
  // under it. Applies to every mark, not only YouTube's.
  const h = height == null ? undefined : Math.max(20, height);
  return (
    <svg
      viewBox={mark.viewBox}
      role="img"
      aria-hidden="true"
      className={cn("block shrink-0", className)}
      style={
        h == null
          ? { minHeight: 20, width: "auto", aspectRatio: String(mark.aspect) }
          : { height: h, width: h * mark.aspect }
      }
    >
      {mark.paths.map((p) => (
        <path key={p.d} d={p.d} fill={p.fill} />
      ))}
    </svg>
  );
}

export function ProviderIcon({
  provider,
  size = "md",
  className,
}: {
  provider: PlatformId;
  size?: keyof typeof sizeClasses;
  className?: string;
}) {
  // Widened to ProviderVisual: PROVIDER_BRAND uses `satisfies`, so its inferred
  // member type omits optional keys the individual entries do not set.
  const visual: ProviderVisual = PROVIDER_BRAND[provider];
  const styles = sizeClasses[size];
  if (visual.mark) {
    /*
      The mark goes INSIDE a wrapper rather than being the positioned box
      itself. An SVG carrying `position:absolute` and padding resolves its
      auto width by shrink-to-fit, which ignores the aspect ratio and
      collapsed the badge to 16×20 — a squashed mark, the exact defect this
      is here to prevent. The wrapper takes the caller's positioning and
      plate; the mark stays in normal flow, sized by height alone.
    */
    return (
      <span
        className={cn("inline-flex items-center justify-center", className)}
      >
        <ProviderMarkSvg mark={visual.mark} height={styles.mark} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md text-white",
        styles.badge,
        className,
      )}
      style={{ backgroundColor: visual.bg }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        role="img"
        aria-hidden="true"
        className={cn("shrink-0", styles.glyph)}
      >
        <path d={visual.path} />
      </svg>
    </span>
  );
}
